import {
  Component,
  effect,
  EventEmitter,
  Inject,
  input,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { SabisChatbotComponent } from '../sabis-chatbot/sabis-chatbot.component';
import { IConfirmationService } from '../shared/confirmation/confirmation.service.interface';
import { HistorySidePanelComponent } from '../sabis-chatbot/history/history-side-panel/history-side-panel.component';
import { ChatHistoryItem, ChatSessionChangedEvent } from '../sabis-chatbot/history/chatbot-history.service';
import { ChatbotService } from '../chatbot-core/chatbot.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ChatbotConfig } from '../../public-api';
import { ChatbotConfigService } from '../chatbot-core/chatbot-config/chatbot-config.service';

@Component({
  selector: 'sabis-chatbot-popup',
  imports: [SabisChatbotComponent, HistorySidePanelComponent, CommonModule],
  templateUrl: './sabis-chatbot-popup.component.html',
  styleUrl: './sabis-chatbot-popup.component.scss'
})
export class SabisChatbotPopupComponent implements OnInit {
  @Input('customCssClass') customCssClass?: string;

  @Output() minimize = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() onSessionChanged = new EventEmitter<ChatSessionChangedEvent>();
  @Output() contentClick = new EventEmitter<{ href: string; category: string | null }>();
  @ViewChild(SabisChatbotComponent) chatbotComponent!: SabisChatbotComponent;
  private _chatbotSessionId: number | null = null;

  @Input() set config(value: ChatbotConfig) {
    this._chatbotConfigService.setConfig(value);
  }

  private _sessionId: number | null = null;

  private _currentSessionId: number | null = null;

  @Input('sessionId') set sessionId(value: number | null) {
    if (value === this._sessionId) return;
    this._currentSessionId = value;
    this._sessionId = value;

    if (value != null) {
      this.selectedHistorySessionId = value;
      this.onSessionChanged.emit({
        action: 'session-changed',
        sessionId: value,
        timestamp: Date.now()
      });
    } else {
      this.selectedHistorySessionId = null;
    }
  }

  public canStartNewSession: boolean = false;
  public isNewChatDisabled: boolean = false;

  /** Signal: the session ID to highlight in the history panel */
  public historyActiveSessionId = signal<string | null>(null);

  /** Signal: set to true when connected to the chatbot API */
  public isHistoryReadyToLoad = signal(false);

  public isHistoryTabVisible: boolean = false;
  public isHistoryPanelCollapsed: boolean = true;


  /** Set when user selects a session from history; passed to sabis-chatbot to load and show messages. */
  public selectedHistorySessionId: number | null = null;

  public chatbotConfig: ChatbotConfig = {
    userFirstName: 'Mario',
    equationPlayerUrl: '',
    isCloseButtonVisible: true,
    isMinimizeButtonVisible: true,
    skipCloseConfirmation: false
  };

  @Input() isSessionIdVisible: boolean = false;

  constructor(
    @Inject('ChatbotDialogService') private _confirmationService: IConfirmationService,
    private _chatbotService: ChatbotService,
    private _chatbotConfigService: ChatbotConfigService
  ) {

    this._chatbotService.isHistoryCollapsedByDefault$
      .pipe(takeUntilDestroyed())
      .subscribe((isHistoryCollapsed) => {
        this.isHistoryPanelCollapsed = isHistoryCollapsed;
      });

    this._chatbotService.isHistoryAllowed$
      .pipe(takeUntilDestroyed())
      .subscribe(isAllowed => {
        this.isHistoryTabVisible = isAllowed;
      });

    effect(() => {
      this.canStartNewSession = this._chatbotService.canStartNewSession();
    });
  }

  ngOnInit(): void {
    this.chatbotConfig.isCloseButtonVisible = this._chatbotConfigService.getConfig()?.isCloseButtonVisible ?? true;
    this.chatbotConfig.isMinimizeButtonVisible = this._chatbotConfigService.getConfig()?.isMinimizeButtonVisible ?? true;
    this.chatbotConfig.userFirstName = this._chatbotConfigService.getConfig()?.userFirstName ?? 'Jad';
  }

  public toggleMinimize(): void {
    this.minimize.emit();
  }

  public attemptCloseChatbot(): void {
    const skipConfirmation = this._chatbotConfigService.getConfig()?.skipCloseConfirmation ?? false;

    if (!skipConfirmation) {

      this._confirmationService.confirmCloseChatbot().subscribe((r) => {
        if (r) {
          this.close.emit();
          if (this.chatbotComponent) {
            this.chatbotComponent.onCloseChatbot();
          }
        }
      });
    } else {
      this.close.emit();
      if (this.chatbotComponent) {
        this.chatbotComponent.onCloseChatbot();
      }
    }
  }

  public onNewChat(): void {
    this.isNewChatDisabled = true;

    this._chatbotService.generateSessionId().subscribe({
      next: (response) => {
        this.chatbotComponent.onNewChatStarted();
        this.onNewSessionCreated(response.session_id);
        this.chatbotComponent.llmSessionId = response.session_id;

        this.onSessionChanged.emit({
          action: 'session-changed',
          sessionId: response.chatbotSessionId,
          timestamp: Date.now()
        });
        this.isNewChatDisabled = false;
      },
      error: (err) => {
        console.error('Failed to start a new chat:', err);
        this.isNewChatDisabled = false;
      },
    });
  }

  public onNewSessionCreated(sessionId: string): void {
    this.historyActiveSessionId.set(sessionId);
  }

  public onConnectionEstablished(sessionId: string | null): void {
    this.historyActiveSessionId.set(sessionId);
    this.isHistoryReadyToLoad.set(true);
    this._chatbotSessionId = this._chatbotService.getActiveChatbotSessionId();

    if (this._currentSessionId != null && this._chatbotSessionId != null && this._currentSessionId !== this._chatbotSessionId) {
      this.onSessionChanged.emit({
        action: 'session-changed',
        sessionId: this._currentSessionId!,
        timestamp: Date.now()
      });
    } else if (this._chatbotSessionId != null) {
      this.onSessionChanged.emit({
        action: 'session-changed',
        sessionId: this._chatbotSessionId!,
        timestamp: Date.now()
      });
    } else if (this._currentSessionId != null) {
      this.isNewChatDisabled = false;
      this.isHistoryTabVisible = true;
      this.canStartNewSession = true;
      this.onSessionChanged.emit({
        action: 'session-changed',
        sessionId: this._currentSessionId!,
        timestamp: Date.now()
      });
    }


  }

  /**
   * Handles selection of a session from history. Passes the selected session to sabis-chatbot
   * so it can show loading, load history, map to messages, and display them.
   */
  public onHistorySessionSelected(item: ChatHistoryItem): void {
    this.selectedHistorySessionId = item.sessionId;
    this.historyActiveSessionId.set(item.llmSessionId);

    this.onSessionChanged.emit({
      action: 'session-changed',
      sessionId: item.sessionId,
      timestamp: Date.now()
    });
  }

  public onContentClick(event: { href: string; category: string | null }): void {
    this.contentClick.emit(event);
  }
}
